// The practice dock's clock: one engine (see backing-track/engine) that the
// metronome and the backing track both play on, so they share a tempo and
// can play together. The tempo is remembered per song.

import { useCallback, useEffect, useRef, useState } from 'react';
import BackingTrackEngine, { clampBpm } from '../backing-track/engine';
import { track } from '../../usage';
import { resumeAudio } from './audio';
import { loadStored, saveStored } from './storage';

const TEMPO_KEY = 'gp-practice-tempo';
// Where the backing track kept its tempos before it shared the clock.
const OLD_TEMPO_KEY = 'gp-backing-track';
const METRONOME_KEY = 'gp-metronome';
const DEFAULT_BPM = 100;

export const MAX_BEATS = 12;
// Clicks per beat: quarters, eighths, triplets, sixteenths.
export const SUBDIVISIONS = [1, 2, 3, 4];

function storedTempos() {
    const stored = loadStored(TEMPO_KEY) || {};
    const old = loadStored(OLD_TEMPO_KEY) || {};
    return {
        bpm: stored.bpm || old.bpm || DEFAULT_BPM,
        songBpm: { ...(old.songBpm || {}), ...(stored.songBpm || {}) },
    };
}

const songTempo = songId => {
    const { bpm, songBpm } = storedTempos();
    return clampBpm(songBpm[songId] || bpm);
};

// accents: per beat, 2 accented, 1 normal, 0 silent.
export const defaultAccents = (beats, previous = []) =>
    Array.from({ length: beats }, (_, i) => previous[i] ?? (i === 0 ? 2 : 1));

function initialMetronome() {
    const stored = loadStored(METRONOME_KEY) || {};
    const beats = Number.isInteger(stored.beats) && stored.beats >= 1 && stored.beats <= MAX_BEATS ? stored.beats : 4;
    const accents = Array.isArray(stored.accents) && stored.accents.length === beats ? stored.accents : defaultAccents(beats);
    const trainer = stored.trainer || {};
    return {
        beats,
        subdivision: SUBDIVISIONS.includes(stored.subdivision) ? stored.subdivision : 1,
        accents,
        volume: typeof stored.volume === 'number' ? stored.volume : 0.8,
        // Speed trainer: `step` BPM faster every `every` bars (or each time
        // round a looped part, 'loop') until `to`.
        trainer: {
            on: !!trainer.on,
            step: trainer.step || 2,
            every: trainer.every === 'loop' ? 'loop' : (trainer.every || 4),
            to: trainer.to || 160,
        },
    };
}

// The speed trainer is for subscribers; for anyone else it stays off even
// if it was left on in this browser.
export default function useTransport(songId, { isSubscriber = false } = {}) {
    const [bpm, setBpmState] = useState(() => songTempo(songId));
    const [beat, setBeat] = useState(null);
    const [layers, setLayers] = useState({ band: false, click: false });
    const [metronome, setMetronomeState] = useState(initialMetronome);
    const engineRef = useRef(null);
    if (!engineRef.current) {
        engineRef.current = new BackingTrackEngine({ onBeat: setBeat, onTempo: setBpmState });
    }
    const engine = engineRef.current;
    const tapsRef = useRef([]);
    // The tempo before the speed trainer sped it up, put back on stop.
    const rampFrom = useRef(null);

    const sync = useCallback(() => {
        setLayers({ band: engine.band, click: engine.click });
        if (!engine.playing) {
            setBeat(null);
            if (rampFrom.current !== null) {
                setBpmState(rampFrom.current);
                rampFrom.current = null;
            }
        }
    }, [engine]);

    const stopAll = useCallback(() => {
        engine.stop();
        sync();
    }, [engine, sync]);

    useEffect(() => () => engine.close(), [engine]);

    // A new song (next song in a songbook) stops the clock and brings back
    // the tempo last used for that song.
    useEffect(() => {
        stopAll();
        setBpmState(songTempo(songId));
    }, [songId, stopAll]);

    useEffect(() => {
        engine.setTempo(bpm);
    }, [engine, bpm]);

    useEffect(() => {
        engine.setMeter({ beats: metronome.beats, subdivision: metronome.subdivision, accents: metronome.accents });
        engine.setVolumes({ click: metronome.volume });
    }, [engine, metronome.beats, metronome.subdivision, metronome.accents, metronome.volume]);

    const { trainer } = metronome;
    const trainerOn = trainer.on && isSubscriber;
    useEffect(() => {
        engine.setRamp(trainerOn ? { step: trainer.step, to: trainer.to, everyBars: trainer.every === 'loop' ? null : trainer.every } : null);
        if (trainerOn && engine.playing && rampFrom.current === null) {
            rampFrom.current = engine.bpm;
        }
    }, [engine, trainerOn, trainer.step, trainer.to, trainer.every]);

    const setBpm = value => {
        const next = clampBpm(value);
        setBpmState(next);
        // A tempo picked by hand is where the speed trainer starts over from.
        if (rampFrom.current !== null) rampFrom.current = next;
        const stored = storedTempos();
        saveStored(TEMPO_KEY, { bpm: next, songBpm: { ...stored.songBpm, [songId]: next } });
    };

    const nudgeTempo = delta => setBpm(bpm + delta);

    const tapTempo = () => {
        const now = performance.now();
        const taps = tapsRef.current.filter(t => now - t < 2500).concat(now).slice(-5);
        tapsRef.current = taps;
        if (taps.length >= 2) {
            const average = (taps[taps.length - 1] - taps[0]) / (taps.length - 1);
            setBpm(60000 / average);
        }
    };

    const setMetronome = changes => {
        setMetronomeState(current => {
            const next = { ...current, ...changes };
            if (changes.beats && changes.beats !== current.beats) {
                next.accents = defaultAccents(changes.beats, current.accents);
            }
            if (changes.trainer) {
                next.trainer = { ...current.trainer, ...changes.trainer };
            }
            saveStored(METRONOME_KEY, next);
            return next;
        });
    };

    // Accent → normal → silent → accent.
    const cycleAccent = index => {
        const accents = metronome.accents.slice();
        accents[index] = (accents[index] + 2) % 3;
        setMetronome({ accents });
    };

    const begin = async layer => {
        resumeAudio();
        if (!engine.playing && trainerOn) {
            rampFrom.current = bpm;
        }
        await engine.start(layer);
        sync();
    };

    const startClick = () => {
        track('metronome_started');
        begin({ click: true });
    };

    const stopClick = () => {
        engine.stopLayers({ click: true });
        sync();
    };

    // The band (with the count-in when the clock isn't already running).
    const startBand = () => begin({ band: true });

    const stopBand = () => {
        engine.stopLayers({ band: true });
        sync();
    };

    return {
        engine,
        bpm,
        beat,
        running: layers.band || layers.click,
        band: layers.band,
        click: layers.click,
        metronome,
        trainerOn,
        setBpm,
        nudgeTempo,
        tapTempo,
        setMetronome,
        cycleAccent,
        startClick,
        stopClick,
        startBand,
        stopBand,
        stopAll,
    };
}
